// Change over 1D / 1W / 1M / 6M / 1Y, for the whole portfolio and per asset class. Ported line for line
// from v1's periodChange() and classChange(). Where v1 built a message with a formatted date
// ("Available from 16 Oct"), this returns the date and the UI formats it the same way.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { addDaysIso, isoOf, now } from './clock';
import { N, cv, cvSgd, type State } from './state';
import { allSales, compute, totals, type Position } from './positions';
import { cashValueOn } from './cashmath';

// 6M was added in v2 (Ben, 13 Oct 2026); the others are v1's.
export const PERIODS: Record<string, [number, string]> = { '1D': [1, 'Today'], '1W': [7, 'Past week'], '1M': [30, 'Past month'], '6M': [182, 'Past 6 months'], '1Y': [365, 'Past year'] };

export type PeriodResult =
  | { na: true; msg: 'Waiting for prices' | 'No holdings'; missL: Position[] }
  | { na: true; msg: 'Builds up from daily values' }
  | { na: true; msg: 'Available from'; from: string }
  | { v: number; pct: number; miss: number; missL: Position[] }
  | { v: number; pct: number; since: string; sinceT?: number; miss: number };

/** The hourly value 1D is measured from (v2, Ben 13 Oct 2026): the last one recorded at or before 24 hours
 *  ago, or, while there isn't one that old yet, the earliest one there is. Null when there are none. */
export function hourlyBase(intraday: any[] | null | undefined, t0 = now()): any | null {
  const pts = (intraday || []).filter((p) => p && Number(p.t) < t0 && Number(p.v) >= 0).sort((a, b) => a.t - b.t);
  if (!pts.length) return null;
  let base = pts[0]; for (const p of pts) if (p.t <= t0 - 24 * 3600e3) base = p;
  return base;
}
/** What 1D is called: "Past 24 hours" when it's measured from the hourly values, else v1's "Today"
 *  (since each holding's previous close). */
export const oneDayLabel = (intraday?: any[] | null) => (hourlyBase(intraday) ? 'Past 24 hours' : PERIODS['1D'][1]);

/** Options: cash = count interest earned on cash balances as part of the change (v2, Ben 13 Oct 2026), so
 *  the change matches the value chart, which includes cash. v1 left cash out; the v1 comparisons don't set it. */
export interface ChangeOpts {
  cash?: boolean;
  /** v2: a class with nothing in it at the starting point, but holdings recorded later with earlier dates
   *  (e.g. a bond bought months ago and added today): their cost at the start counts as money put in, so
   *  adding them isn't shown as a gain. */
  late?: boolean;
}
/** Cost (SGD) of a class's holdings at the end of day d, from the trades dated on or before it. */
function costAt(S: State, k: string, d: string, strict = false): number {
  const on = (x: string) => (strict ? x < d : x <= d);
  return totals(compute({ ...S, display: 'SGD', txns: S.txns.filter((t) => on(t.date)), income: S.income.filter((i) => on(i.date)) }), k).cost;
}
/** sameDay: that day's trades are already counted as money put in, so only earlier ones are added here. */
const lateIn = (S: State, k: string, then: number, d: string, o: ChangeOpts, sameDay = false) => (o.late && !(then > 0) ? costAt(S, k, d, sameDay) : 0);
/** Interest earned on the cash balances since a day, in SGD: each balance's value today minus its value on
 *  that day (or on its "as of" date, if later, since a balance set after the day is money put in, not interest). */
export function cashInterestSince(S: State, d: string): number {
  const td = isoOf(now());
  return S.cash.reduce((a, c) => { const from = c.asOf && c.asOf > d ? c.asOf : d; return from >= td ? a : a + cvSgd(S, cashValueOn(c, td) - cashValueOn(c, from), c.ccy); }, 0);
}
/** v2 (Ben, 15 Oct 2026): were the trades dated on a starting value's own day recorded after that value
 *  was taken? Trades only have a date, so a purchase made later on that day used to be taken as already
 *  held, and its whole value showed as a gain (PEP, bought the evening after the 1D starting point). The
 *  value's recorded cost decides: whichever is nearer, the cost of the holdings without that day's trades or
 *  with them (both at the value's own exchange rate). */
export function dayTradesAfter(S: State, d: string, thenCost: unknown, fx: unknown): boolean {
  const c = Number(thenCost);
  if (!(thenCost != null && isFinite(c)) || !S.txns.some((t) => t.date === d)) return false;
  const S2 = { ...S, display: 'SGD' as const, fx: Number(fx) > 0 ? { ...(S.fx || { date: '', src: 'live', at: 0 }), rate: Number(fx) } : S.fx };
  const costTo = (strict: boolean) => totals(compute({ ...S2, txns: S.txns.filter((t) => (strict ? t.date < d : t.date <= d)), income: S.income.filter((i) => (strict ? i.date < d : i.date <= d)) })).cost;
  return Math.abs(c - costTo(true)) < Math.abs(c - costTo(false));
}
/** Money put into one class since a moment's day (buys and staking rewards, less sale proceeds), in SGD.
 *  sameDay: the trades dated on that day came after the moment, so they count too. */
function flowsSince(S: State, k: string, d: string, sameDay = false): [number, number] {
  let net = 0, buys = 0;
  const after = (x: string) => x > d || (sameDay && x === d);
  for (const t of S.txns) {
    if (t.type !== k || !after(t.date)) continue; const amt = t.type === 'bond' ? N(t.qty) * N(t.price) / 100 : N(t.qty) * N(t.price), f = N(t.fee);   // v2: bonds are priced per 100
    if (t.side === 'buy') { const x = cvSgd(S, amt + f, t.ccy); net += x; buys += x; } else net -= cvSgd(S, amt - f, t.ccy);
  }
  for (const i of S.income) { if (i.type !== k || i.kind !== 'staking' || i.addToHoldings === false || !after(i.date)) continue; const x = cvSgd(S, N(i.gross), i.ccy); net += x; buys += x; }
  return [net, buys];
}
/** Per class, the change in gain or loss since an hourly value: value now − value then − money put in
 *  since (trades dated after that value's day). Summed over the classes, this is the 1D total, so the
 *  hero, the Over time table and the chart all start from the same point. */
function hourlyClassChange(S: State, base: any, o: ChangeOpts): Record<string, { v: number; then: number; buys: number } | null> {
  const q = compute({ ...S, display: 'SGD' }), d = isoOf(base.t), out: Record<string, { v: number; then: number; buys: number } | null> = {};
  const sameDay = !!o.late && dayTradesAfter(S, d, base.cost, base.fx);
  for (const [k] of classesFor(S)) {
    const nowV = totals(q, k).mv, then = N(base[k]), [net0, buys0] = flowsSince(S, k, d, sameDay), late = lateIn(S, k, then, d, o, sameDay), net = net0 + late, buys = buys0 + late;
    out[k] = then > 0 || nowV > 0 || net ? { v: nowV - then - net, then, buys } : null;
  }
  if (o.cash) out.cash = S.cash.length || N(base.cash) > 0 ? { v: cashInterestSince(S, d), then: N(base.cash), buys: 0 } : null;
  return out;
}

/** Change in gain/loss over a period, so money added or withdrawn doesn't count:
 *  (value − cost) now, minus (value − cost) at the start, plus gains realized from sales in between.
 *  1D: with hourly values, the change since the hourly value from 24 hours ago (see hourlyClassChange);
 *  without them, v1's change since each holding's previous close. */
export function periodChange(S: State, ps: Position[], per: string, intraday?: any[] | null, o: ChangeOpts = {}): PeriodResult {
  const base = per === '1D' ? hourlyBase(intraday) : null;
  if (base) {
    const cc = hourlyClassChange(S, base, o), xs = Object.values(cc).filter(Boolean) as { v: number; then: number; buys: number }[];
    const v = xs.reduce((a, x) => a + x.v, 0), b = xs.reduce((a, x) => a + x.then + x.buys, 0);
    return { v: cv(S, v, 'SGD'), pct: b > 0 ? v / b * 100 : NaN, since: isoOf(base.t), sinceT: base.t, miss: totals(compute({ ...S, display: 'SGD' })).missing };
  }
  if (per === '1D') {
    let v = 0, base = 0, n = 0; const missL = ps.filter((p) => p.dayMissing), miss = missL.length;
    for (const p of ps) if (p.day != null && (p.qty > 0 || p.dayBase > 0 || Math.abs(p.day) > 0)) { v += p.day; base += p.dayBase; n++; }
    if (!n) return { na: true, msg: miss ? 'Waiting for prices' : 'No holdings', missL };
    return { v, pct: base > 0 ? v / base * 100 : NaN, miss, missL };
  }
  const n = PERIODS[per][0], H = S.history || [], target = isoOf(now() - n * 864e5);
  let h: any = null; for (const x of H) if (x.d <= target) h = x;
  if (!h) { if (!H.length) return { na: true, msg: 'Builds up from daily values' }; return { na: true, msg: 'Available from', from: addDaysIso(H[0].d, n) }; }
  const sg = { ...S, display: 'SGD' as const };
  const q = compute(sg), t = totals(q), missing = t.missing, nowG = t.mv - t.cost, rz = allSales(q).filter((x) => x.date > h.d).reduce((a, x) => a + x.gain, 0);
  const thenV = h.sgd - (h.cash || 0), dSGD = nowG - (thenV - h.cost) + rz + (o.cash ? cashInterestSince(S, h.d) : 0), b0 = thenV + (o.cash ? N(h.cash) : 0);
  return { v: cv(S, dSGD, 'SGD'), pct: b0 > 0 ? dSGD / b0 * 100 : NaN, since: h.d, miss: missing };
}

export const CLASSES: [string, string][] = [['stock', 'Stocks'], ['crypto', 'Crypto'], ['metal', 'Commodities'], ['bond', 'Bonds']];
/** The classes to show: Bonds (v2) only when there are bond trades. */
export const classesFor = (S: State): [string, string][] => CLASSES.filter(([k]) => k !== 'bond' || S.txns.some((t) => t.type === 'bond'));
export interface ClassChange { out: Record<string, Record<string, { v: number; pct: number } | null>>; notes: Record<string, string | null> }

/** Change in gain or loss per class over each period: value now − value at the start − money put in since
 *  (buys and staking rewards, less sale proceeds). The start value comes from the daily snapshots, in SGD. */
export function classChange(S: State, ps: Position[], intraday?: any[] | null, o: ChangeOpts = {}): ClassChange {
  const out: ClassChange['out'] = {}, H = S.history || [], base = hourlyBase(intraday), hc = base ? hourlyClassChange(S, base, o) : null;
  for (const [k] of classesFor(S)) {
    if (hc) { const x = hc[k]; out[k] = { '1D': x ? { v: cv(S, x.v, 'SGD'), pct: x.then + x.buys > 0 ? x.v / (x.then + x.buys) * 100 : NaN } : null }; continue; }
    let v = 0, b = 0, n = 0; for (const p of ps) if (p.type === k && p.day != null && (p.qty > 0 || p.dayBase > 0 || Math.abs(p.day) > 0)) { v += p.day; b += p.dayBase; n++; }
    out[k] = { '1D': n ? { v, pct: b > 0 ? v / b * 100 : NaN } : null };
  }
  // Cash (with the cash option): interest earned over each period, measured from the same starting point.
  if (o.cash) { const x = hc && hc.cash; out.cash = { '1D': x ? { v: cv(S, x.v, 'SGD'), pct: x.then > 0 ? x.v / x.then * 100 : NaN } : null }; }
  const nowV: Record<string, number> = {};
  const q = compute({ ...S, display: 'SGD' }); for (const [k] of classesFor(S)) nowV[k] = totals(q, k).mv;
  const notes: ClassChange['notes'] = {};
  for (const per of Object.keys(PERIODS).filter((p) => p !== '1D')) {
    const n = PERIODS[per][0], target = isoOf(now() - n * 864e5); let h: any = null; for (const x of H) if (x.d <= target) h = x;
    if (!h) { notes[per] = H.length ? addDaysIso(H[0].d, n) : null; classesFor(S).forEach(([k]) => { out[k][per] = null; }); if (o.cash) out.cash[per] = null; continue; }
    const sameDay = !!o.late && dayTradesAfter(S, h.d, h.cost, h.fx);
    for (const [k] of classesFor(S)) {
      const then = N(h[k]), [net0, buys0] = flowsSince(S, k, h.d, sameDay), late = lateIn(S, k, then, h.d, o, sameDay), net = net0 + late, buys = buys0 + late, g = nowV[k] - then - net;
      out[k][per] = then > 0 || nowV[k] > 0 || net ? { v: cv(S, g, 'SGD'), pct: then + buys > 0 ? g / (then + buys) * 100 : NaN } : null;
    }
    if (o.cash) { const then = N(h.cash), g = cashInterestSince(S, h.d); out.cash[per] = S.cash.length || then > 0 ? { v: cv(S, g, 'SGD'), pct: then > 0 ? g / then * 100 : NaN } : null; }
  }
  return { out, notes };
}

/** The Total row of the Over time view: sum of changes over the sum of each class's implied base. */
export function classTotals(ps: Position[], cc: ClassChange): Record<string, { v: number; pct: number } | null> {
  const act = CLASSES.filter(([k]) => ps.some((p) => p.type === k && (p.qty > 0 || p.dayBase > 0)));
  const tot: Record<string, { v: number; pct: number } | null> = {};
  Object.keys(PERIODS).forEach((per) => {
    const xs = act.map(([k]) => cc.out[k][per]).filter(Boolean) as { v: number; pct: number }[];
    if (!xs.length) { tot[per] = null; return; }
    let v = 0, b = 0; xs.forEach((x) => { v += x.v; if (isFinite(x.pct) && x.pct !== 0) b += x.v / (x.pct / 100); }); tot[per] = { v, pct: b > 0 ? v / b * 100 : NaN };
  });
  return tot;
}
